import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { TableHead } from '@/components/ui/table'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { CommitmentMatrixDashboardSortDirection } from '@/types'

export interface SortState<TColumn extends string> {
	column: TColumn
	direction: CommitmentMatrixDashboardSortDirection
}

export function SortableHead<TColumn extends string>({
	column,
	sort,
	onSort,
	align,
	tooltip,
	children,
}: {
	column: TColumn
	sort: SortState<TColumn>
	onSort: (column: TColumn) => void
	align?: 'right'
	/** Explicação curta exibida ao passar o mouse sobre o cabeçalho */
	tooltip?: string
	children: React.ReactNode
}) {
	const isActive = sort.column === column
	const Icon = !isActive
		? ArrowUpDown
		: sort.direction === 'asc'
			? ArrowUp
			: ArrowDown

	const button = (
		<button
			type="button"
			onClick={() => onSort(column)}
			className={cn(
				'inline-flex items-center gap-1 hover:text-kaefer-dark-grey',
				align === 'right' && 'flex-row-reverse',
				isActive && 'text-kaefer-dark-grey',
			)}
		>
			<span>{children}</span>
			<Icon className="size-3.5" />
		</button>
	)

	return (
		<TableHead className={align === 'right' ? 'text-right' : undefined}>
			{tooltip ? (
				<Tooltip>
					<TooltipTrigger asChild>{button}</TooltipTrigger>
					<TooltipContent>{tooltip}</TooltipContent>
				</Tooltip>
			) : (
				button
			)}
		</TableHead>
	)
}

export function nextSort<TColumn extends string>(
	current: SortState<TColumn>,
	column: TColumn,
): SortState<TColumn> {
	if (current.column !== column) return { column, direction: 'asc' }
	return { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
}
